import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, X } from 'lucide-react';
import { VEHICLE_TYPES, SERVICE_INTERVALS } from './VehicleFormDialog';

const BulkVehicleDialog = ({
  open, onOpenChange, bulkVehicles, saving,
  onSubmit, onAdd, onRemove, onUpdate,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-heading">Add Multiple Vehicles</DialogTitle>
        <DialogDescription>Add multiple vehicles at once to your fleet</DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4 mt-4">
        {bulkVehicles.map((vehicle, index) => (
          <Card key={vehicle._key ?? index} className="relative">
            <CardContent className="p-4">
              {bulkVehicles.length > 1 && (
                <Button type="button" variant="ghost" size="icon" className="absolute top-2 right-2" onClick={() => onRemove(index)} data-testid={`remove-vehicle-${index}`}>
                  <X className="w-4 h-4" />
                </Button>
              )}
              <h4 className="font-medium text-slate-700 mb-3">Vehicle {index + 1}</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input value={vehicle.name} onChange={(e) => onUpdate(index, 'name', e.target.value)} placeholder="Vehicle name" data-testid={`bulk-name-${index}`} />
                </div>
                <div className="space-y-2">
                  <Label>Registration *</Label>
                  <Input value={vehicle.registration} onChange={(e) => onUpdate(index, 'registration', e.target.value)} placeholder="ABC-1234" className="font-data" data-testid={`bulk-registration-${index}`} />
                </div>
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select value={vehicle.vehicle_type} onValueChange={(value) => onUpdate(index, 'vehicle_type', value)}>
                    <SelectTrigger data-testid={`bulk-type-${index}`}><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>{VEHICLE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Make</Label>
                  <Input value={vehicle.make} onChange={(e) => onUpdate(index, 'make', e.target.value)} placeholder="Toyota" data-testid={`bulk-make-${index}`} />
                </div>
                <div className="space-y-2">
                  <Label>Model</Label>
                  <Input value={vehicle.model} onChange={(e) => onUpdate(index, 'model', e.target.value)} placeholder="Hilux" data-testid={`bulk-model-${index}`} />
                </div>
                <div className="space-y-2">
                  <Label>Service Interval</Label>
                  <Select value={vehicle.service_interval_km?.toString() || '5000'} onValueChange={(value) => onUpdate(index, 'service_interval_km', parseInt(value))}>
                    <SelectTrigger data-testid={`bulk-interval-${index}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{SERVICE_INTERVALS.map((i) => <SelectItem key={i.value} value={i.value.toString()}>{i.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Last Service Odometer</Label>
                  <Input type="number" value={vehicle.last_service_odometer} onChange={(e) => onUpdate(index, 'last_service_odometer', e.target.value)} placeholder="e.g., 50000" className="font-data" data-testid={`bulk-odometer-${index}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        <Button type="button" variant="outline" onClick={onAdd} className="w-full" data-testid="add-another-vehicle-btn">
          <Plus className="w-4 h-4 mr-2" />Add Another Vehicle
        </Button>
        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" className="bg-gray-800 hover:bg-gray-700" disabled={saving} data-testid="save-bulk-vehicles-btn">
            {saving ? 'Saving...' : `Add ${bulkVehicles.length} Vehicle${bulkVehicles.length > 1 ? 's' : ''}`}
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

export default BulkVehicleDialog;
